import { useMemo, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { categories, featuredStories, latestStories } from '@/mocks/homeData';

interface FeedItem {
  id: string;
  category: string;
  region: string;
  title: string;
  date: string;
  views: string;
}

export default function LatestSection() {
  const feed: FeedItem[] = useMemo(
    () => [
      ...latestStories,
      ...featuredStories.map((f) => ({
        id: f.id,
        category: f.category,
        region: f.region,
        title: f.title,
        date: f.date,
        views: f.views,
      })),
    ],
    [],
  );

  const tabs = useMemo(() => ['全部', ...categories.map((c) => c.name)], []);
  const [activeTab, setActiveTab] = useState('全部');

  const visible = useMemo(
    () => (activeTab === '全部' ? feed : feed.filter((item) => item.category === activeTab)),
    [feed, activeTab],
  );

  return (
    <section id="latest" className="scroll-mt-24 bg-background-100 py-16 md:py-24">
      <div className="mx-auto px-4 md:px-10">
        <SectionHeading
          eyebrow="LATEST FEED"
          title="最新收录"
          desc="按主题筛选，快速找到你想看的那一类怪事。"
        />

        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                aria-pressed={isActive}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <ul className="mt-8 overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50">
          {visible.map((item, index) => (
            <li key={item.id} className="border-b border-foreground-950/10 last:border-b-0">
              <div className="group flex items-start gap-4 p-5 transition-colors hover:bg-background-100 md:items-center md:gap-6 md:px-7">
                <span className="w-8 shrink-0 font-heading text-xl font-black text-foreground-950/20 md:text-2xl">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:gap-6">
                  <div className="flex shrink-0 items-center gap-2 md:w-48 md:flex-col md:items-start md:gap-1">
                    <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                      {item.category}
                    </span>
                    <span className="text-xs text-foreground-500">{item.region}</span>
                  </div>
                  <h3 className="flex-1 font-heading text-base font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    {item.title}
                  </h3>
                  <div className="flex shrink-0 items-center gap-4 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line"></i>
                      {item.date}
                    </span>
                    <span className="hidden items-center gap-1.5 sm:inline-flex">
                      <i className="ri-eye-line"></i>
                      {item.views}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {visible.length === 0 && (
            <li className="p-10 text-center text-sm text-foreground-500">该主题下暂时还没有收录内容。</li>
          )}
        </ul>

        <p className="mt-5 text-center text-xs text-foreground-500">
          当前主题「{activeTab}」共 {visible.length} 篇 · 数据每日更新
        </p>
      </div>
    </section>
  );
}